import PropTypes from 'prop-types';
import React, { useEffect, useRef, useState } from 'react';
import { Button, Card, Col, Row, Spinner } from 'react-bootstrap';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import mascotCool from '../../assets/mascot-cool.png';
import { getRequestAsync, postRequestAsync } from '../../helpers/apiHandler';
import { useWhatsAppData } from '../../hooks/useWhatsAppData';
import { showAlert } from '../../store/actions/alertAction';
import './_sandboxConnectionWizard.scss';
import BusinessMessageForm from './BusinessMessageForm';
import SandboxStepper from './SandboxStepper';
import UserMessageForm from './UserMessageForm';

const SandboxConnectionWizard = ({ phoneNumber, accessCode }) => {
  const { access_token } = useSelector((state) => state.auth.user);
  const [activeStep, setActiveStep] = useState(1);
  const [isPolling, setIsPolling] = useState(true);
  const [isBound, setIsBound] = useState(false);
  const [boundPhone, setBoundPhone] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [wabaData, setWabaData] = useState(null);
  const pollingIntervalRef = useRef(null);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const {
    data: waCombinedData,
    isLoading: isLoadingWaData,
    refetch,
  } = useWhatsAppData('get-combined-data', access_token?.token);

  useEffect(() => {
    if (waCombinedData && !isLoadingWaData) {
      setWabaData(waCombinedData);
    }
  }, [waCombinedData, isLoadingWaData]);

  useEffect(() => {
    if (activeStep === 4 && refetch) {
      refetch();
    }
  }, [activeStep, refetch]);

  useEffect(() => {
    if (activeStep === 1 && isPolling && !isBound) {
      checkAccessCodeBinding();
      pollingIntervalRef.current = setInterval(checkAccessCodeBinding, 5000);
    }

    return () => {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
      }
    };
  }, [activeStep, isPolling, isBound, accessCode]);

  useEffect(() => {
    if (isBound && boundPhone) {
      setIsPolling(false);
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
      }
      dispatch(showAlert(`Successfully connected with WhatsApp number ${boundPhone}`, 'success'));
      setActiveStep(2);
    }
  }, [isBound, boundPhone, dispatch]);

  const handleGoToWhatsApp = () => {
    const data = wabaData || waCombinedData;
    if (data && data.data.combinedData && data.data.combinedData.length > 0) {
      const firstAccount = data.data.combinedData[0];
      if (
        firstAccount.phoneNumbers &&
        firstAccount.phoneNumbers.data &&
        firstAccount.phoneNumbers.data.length > 0
      ) {
        navigate(
          `/dashboard/whatsapp/phone-number-details/${firstAccount.waba_id}/${firstAccount.phoneNumbers.data[0].id}`
        );
      } else {
        navigate('/dashboard/whatsapp/getting-started');
      }
    } else {
      navigate('/dashboard/whatsapp');
    }
  };

  const checkAccessCodeBinding = async () => {
    try {
      const response = await getRequestAsync(
        `/v2/whatsapp/check-access-code-binding?access_code=${accessCode}`,
        {
          headers: { Authorization: access_token.token },
        }
      );

      if (response.success && response.data.isBound) {
        setIsBound(true);
        setBoundPhone(response.data.boundPhone);
      }
    } catch (error) {
      console.error('Error checking access code binding:', error);
    }
  };

  const handleOpenWhatsApp = () => {
    const formattedNumber = phoneNumber.replace(/\D/g, '');
    window.open(
      `https://api.whatsapp.com/send?phone=${formattedNumber}&text=Access-Now%20${accessCode}`,
      '_blank'
    );
  };

  const handleCopy = (text, type) => {
    navigator.clipboard.writeText(text);
    dispatch(showAlert(`${type} copied to clipboard`, 'success'));
  };

  const handleNextStep = () => {
    if (activeStep < 4) {
      setActiveStep(activeStep + 1);
    }
  };

  const handlePreviousStep = () => {
    if (activeStep > 1) {
      setActiveStep(activeStep - 1);
    }
  };

  const handleBusinessMessageSubmit = async (formData) => {
    const payload = {
      template_name: formData.template,
      recipient_number: formData.to,
      language: 'en_US',
      components: [],
    };

    setIsLoading(true);

    try {
      const response = await postRequestAsync(
        '/v2/whatsapp/send-sandbox-template',
        payload,
        access_token.token
      );

      if (response.success) {
        dispatch(showAlert('Template message sent successfully!', 'success'));
        handleNextStep();
      } else {
        dispatch(showAlert(response.message || 'Failed to send template message', 'danger'));
      }
    } catch (error) {
      console.error('Error sending template message:', error);
      dispatch(showAlert('Failed to send template message', 'danger'));
    } finally {
      setIsLoading(false);
    }
  };

  const handleUserMessageSubmit = async (formData) => {
    console.log('User message form data:', formData);

    const payload = {
      message: formData.message,
      recipient_number: boundPhone,
      preview_url: false,
    };

    setIsLoading(true);

    try {
      const response = await postRequestAsync(
        '/v2/whatsapp/send-sandbox-text',
        payload,
        access_token.token
      );

      if (response.success) {
        dispatch(showAlert('Reply sent successfully!', 'success'));
        handleNextStep();
      } else {
        dispatch(showAlert(response.message || 'Failed to send reply', 'danger'));
      }
    } catch (error) {
      console.error('Error sending reply message:', error);
      dispatch(showAlert('Failed to send reply', 'danger'));
    } finally {
      setIsLoading(false);
    }
  };

  const renderStepContent = () => {
    switch (activeStep) {
      case 1:
        return (
          <div className="sandbox-wizard-content">
            <h3 className="sandbox-wizard-title">Connect to WhatsApp Sandbox</h3>
            <p className="sandbox-wizard-description">
              To start testing, send a WhatsApp message from your device to the elfoA2P number to
              connect with the sandbox.
            </p>

            <Row className="mt-5">
              <Col md={6} className="sandbox-wizard-send-message text-center">
                <h4 className="mb-4">Send a WhatsApp message</h4>
                <p>Send a WhatsApp message from your device to</p>

                <div className="phone-number-display d-flex align-items-center justify-content-center mb-3">
                  <i className="fab fa-whatsapp mr-2 text-success fa-2x"></i>
                  <span className="phone-number mr-2">{phoneNumber}</span>
                  <Button
                    variant="link"
                    className="copy-btn ms-2 p-0"
                    onClick={() => handleCopy(phoneNumber, 'Phone number')}
                  >
                    <i className="fas fa-copy"></i>
                  </Button>
                </div>

                <div className="access-code-display mt-4 mb-4">
                  <div className="d-flex align-items-center justify-content-center">
                    <span className="mr-2">and enter this code: </span>
                    <strong className="access-code mr-2">{`Access-Now ` + accessCode}</strong>
                    <Button
                      variant="link"
                      className="copy-btn ps-4 ms-2 p-0"
                      onClick={() => handleCopy(`Access-Now ${accessCode}`, 'Access code')}
                    >
                      <i className="fas fa-copy"></i>
                    </Button>
                  </div>
                </div>

                <Button
                  className="open-whatsapp-btn mt-3 ml-auto mr-auto"
                  variant="success"
                  onClick={handleOpenWhatsApp}
                >
                  Open WhatsApp
                </Button>
              </Col>

              <Col md={6} className="sandbox-wizard-qr-code">
                <div className="divider-or d-md-flex d-none">
                  <div className="divider-line"></div>
                  <div className="divider-text">Or</div>
                  <div className="divider-line"></div>
                </div>

                <div className="d-md-none d-block text-center my-4">
                  <strong>OR</strong>
                </div>

                <div className="qr-container">
                  <h4 className="mb-4">Use your mobile device to scan the QR code.</h4>
                  <div className="qr-code">
                    <img
                      src={`https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=https://wa.me/${phoneNumber.replace(
                        /\D/g,
                        ''
                      )}?text=Access-Now%20${accessCode}`}
                      alt="WhatsApp QR Code"
                      className="img-fluid"
                    />
                  </div>
                </div>
              </Col>
            </Row>
          </div>
        );
      case 2:
        return (
          <Row>
            <Col md={12}>
              <BusinessMessageForm
                phoneNumber={phoneNumber}
                boundPhone={boundPhone}
                onSubmit={handleBusinessMessageSubmit}
                onBack={handlePreviousStep}
                onSkip={handleNextStep}
                isLoading={isLoading}
              />
            </Col>
          </Row>
        );
      case 3:
        return (
          <Row>
            <Col md={12}>
              <UserMessageForm
                phoneNumber={phoneNumber}
                onSubmit={handleUserMessageSubmit}
                onBack={handlePreviousStep}
                onSkip={handleNextStep}
                isLoading={isLoading}
                boundedPhone={boundPhone}
              />
            </Col>
          </Row>
        );
      case 4:
        return (
          <div className="completion-step">
            <div className="completion-content">
              <div className="mascot-container">
                <img src={mascotCool} alt="Mascot" className="mascot-image" />
              </div>
              <div className="completion-text-container">
                <h3 className="completion-title">All good!</h3>
                <p className="completion-description">
                  Congratulations on completing the WhatsApp Sandbox! What&apos;s next?
                </p>
                <p className="completion-description">
                  To exit the sandbox, you can reply with &apos;STOP&apos; at any time. Otherwise, if there is no response within 24 hours, you will automatically exit the sandbox. Please note that reactivation is required if you wish to use the sandbox again.
                </p>
                <div className="completion-actions">
                  <Button
                    variant="ghost"
                    onClick={handlePreviousStep}
                    className="back-btn"
                    disabled={isLoadingWaData}
                  >
                    Back
                  </Button>
                  <Button
                    variant="primary"
                    className="primary-btn"
                    onClick={handleGoToWhatsApp}
                    disabled={isLoadingWaData}
                  >
                    {isLoadingWaData ? (
                      <>
                        <Spinner
                          as="span"
                          animation="border"
                          size="sm"
                          role="status"
                          aria-hidden="true"
                          className="me-2"
                        />
                        Loading...
                      </>
                    ) : (
                      'Go to WhatsApp Account List'
                    )}
                  </Button>
                </div>
              </div>
            </div>
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <Card className="sandbox-wizard-card border-0 shadow-sm mb-4">
      <Card.Body className="p-4">
        <SandboxStepper activeStep={activeStep} />
        {renderStepContent()}
      </Card.Body>
    </Card>
  );
};

SandboxConnectionWizard.propTypes = {
  phoneNumber: PropTypes.string.isRequired,
  accessCode: PropTypes.string.isRequired,
};

export default SandboxConnectionWizard;
